/**
 * UX Physics Layout - Persistent canvas with hot-swappable stages
 *
 * This layout provides a single BabylonJS canvas that persists across
 * child route changes. The StageManager handles hot-swapping assets
 * when navigating between /gem, /bob, and /rune.
 *
 * Key benefits:
 * - Inspector stays open across route changes
 * - No engine/scene recreation on navigation
 * - Shared physics infrastructure (Havok WASM loaded once)
 */

import type { Scene } from "@babylonjs/core";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { BabylonCanvas, BabylonProvider } from "@tony/nl-babylon/react";
import { Plus, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { InspectorToggleButton } from "@/babylon/components/controls/InspectorToggleButton";
import {
  StageProvider,
  useStage,
  useStageInitializer,
} from "@/babylon/context/StageContext";
import { registerAllStagePacks } from "@/babylon/stages";
import { useDebug } from "@/context/DebugContext";
import { cn } from "@/utils/cn";

export const Route = createFileRoute("/_immersive/demo/3d/ux")({
  component: UxPhysicsLayout,
});

const PHYSICS_TABS = [
  { id: "gem", label: "Gem", path: "/demo/3d/ux/gem" },
  { id: "bob", label: "Bob", path: "/demo/3d/ux/bob" },
  { id: "rune", label: "Rune", path: "/demo/3d/ux/rune" },
] as const;

// Register all stage packs at module load time
registerAllStagePacks();

function UxPhysicsLayout() {
  return (
    <StageProvider>
      <BabylonProvider>
        <UxPhysicsLayoutInner />
      </BabylonProvider>
    </StageProvider>
  );
}

function UxPhysicsLayoutInner() {
  const { isDebugEnabled } = useDebug();
  const { initializeManagers } = useStageInitializer();
  const { isLoading, loadStage, resetStage, isInitialized } = useStage();
  const [spinningTab, setSpinningTab] = useState<string | null>(null);

  const handleSceneReady = useCallback(
    async (scene: Scene) => {
      console.log("[UxLayout] SCENE_READY - initializing managers");
      await initializeManagers(scene);
    },
    [initializeManagers],
  );

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <nav className="shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-2 py-1">
        <div className="flex gap-1 items-center">
          {/* Back button */}
          <Link
            className={cn(
              "px-2 py-1.5 text-xs font-medium rounded-md",
              "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800",
            )}
            to=".."
          >
            ← Back
          </Link>
          <span className="text-gray-300 dark:text-gray-600 mx-1">|</span>
          {PHYSICS_TABS.map((tab) => (
            <Link className="flex items-center" key={tab.path} to={tab.path}>
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium rounded-l-md",
                      isActive
                        ? "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:bg-gray-300 dark:active:bg-gray-600 active:scale-[0.98]",
                    )}
                  >
                    {tab.label}
                  </span>
                  <button
                    className={cn(
                      "h-7 px-1.5 text-xs font-medium rounded-r-md border-l border-gray-200 dark:border-gray-700",
                      "bg-gray-100 dark:bg-gray-800 select-none cursor-pointer flex items-center justify-center",
                      isActive
                        ? "text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:scale-[0.98]"
                        : "text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 active:scale-[0.98]",
                    )}
                    disabled={!isInitialized}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isActive) {
                        setSpinningTab(tab.id);
                        resetStage(tab.id);
                        setTimeout(() => setSpinningTab(null), 400);
                      } else {
                        loadStage(tab.id, "insert");
                      }
                    }}
                    title={
                      isActive
                        ? `Reset ${tab.label} scene`
                        : `Add more ${tab.label} to scene`
                    }
                    type="button"
                  >
                    {isActive ? (
                      <RotateCcw
                        className={cn(
                          "size-3.5",
                          spinningTab === tab.id &&
                            "animate-[spin-ccw_0.4s_ease-out]",
                        )}
                      />
                    ) : (
                      <Plus className="size-3.5" />
                    )}
                  </button>
                </>
              )}
            </Link>
          ))}
          {isLoading && (
            <span className="ml-2 text-xs text-gray-500">Loading...</span>
          )}
          <div className="ml-auto">
            <InspectorToggleButton />
          </div>
        </div>
      </nav>

      {/* Canvas Container */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {/* Persistent Babylon Canvas */}
        <BabylonCanvas
          className="w-full h-full"
          debugMode={isDebugEnabled}
          onSceneReady={handleSceneReady}
          sceneId="ux-physics"
        />

        {/* Outlet for child routes - they call loadStage() on mount */}
        <div className="absolute inset-0 pointer-events-none">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/**
 * Hook for child routes to load their stage on mount
 */
export function useLoadStageOnMount(stageId: string) {
  const { loadStage, isInitialized } = useStage();

  useEffect(() => {
    if (isInitialized) {
      loadStage(stageId);
    }
  }, [isInitialized, loadStage, stageId]);
}
